<template>
	<template v-if="triggerMode === 'hover'">
		<div
			class="relative"
			@mouseenter="isOpen = true"
			@mouseleave="isOpen = false"
		>
			<MenuItemTrigger
				:blok="blok"
				:open="isOpen"
				:get-link="getLink"
				:is-active-route="isActiveRoute"
				cursor-default
				@close-mobile-menu="$emit('closeMobileMenu')"
				@navigate-to-menu="$emit('navigateToMenu', $event)"
			/>
			<transition
				v-if="blok.children?.length"
				enter-active-class="transition ease-out duration-600"
				enter-from-class="opacity-0 translate-y-1"
				enter-to-class="opacity-100 translate-y-0"
				leave-active-class="transition ease-in duration-300"
				leave-from-class="opacity-100 translate-y-0"
				leave-to-class="opacity-0 translate-y-1"
			>
				<MenuItemPanel
					v-show="isOpen"
					:blok="blok"
					:get-link="getLink"
					@close="onPanelClose(() => (isOpen = false))"
				/>
			</transition>
		</div>
	</template>

	<Popover v-else class="relative">
		<PopoverButton
			:id="`popover-button-${blok._uid}`"
			v-slot="{ open }"
			class="w-full flex items-center gap-1 text-sm uppercase text-current outline-none lg:hover:border-white"
		>
			<MenuItemTrigger
				:blok="blok"
				:open="open"
				:get-link="getLink"
				:is-active-route="isActiveRoute"
				@close-mobile-menu="$emit('closeMobileMenu')"
				@navigate-to-menu="$emit('navigateToMenu', $event)"
			/>
		</PopoverButton>

		<transition
			v-if="blok.children?.length"
			enter-active-class="transition ease-out duration-600"
			enter-from-class="opacity-0 translate-y-1"
			enter-to-class="opacity-100 translate-y-0"
			leave-active-class="transition ease-in duration-300"
			leave-from-class="opacity-100 translate-y-0"
			leave-to-class="opacity-0 translate-y-1"
		>
			<PopoverPanel :id="`popover-panel-${blok._uid}`" v-slot="{ close }">
				<MenuItemPanel
					:blok="blok"
					:get-link="getLink"
					@close="onPanelClose(close)"
				/>
			</PopoverPanel>
		</transition>
	</Popover>
</template>

<script setup lang="ts">
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import type { BlokMenuItem } from "~/types/storyblok/storyblok";
import MenuItemTrigger from "./MenuItemTrigger.vue";
import MenuItemPanel from "./MenuItemPanel.vue";

interface Props {
	blok: BlokMenuItem;
	triggerMode?: "click" | "hover";
}

const emit = defineEmits<{
	closeMobileMenu: [];
	navigateToMenu: [payload: { Children: BlokMenuItem["children"] }];
}>();

const { blok, triggerMode = "click" } = defineProps<Props>();

const isOpen = ref(false);

const localePath = useLocalePath();
const route = useRoute();

const getLink = (link: BlokMenuItem["link"]) =>
	localePath(`/${link?.story?.full_slug}`);

const isActiveRoute = computed(() => {
	if (route.path.includes(getLink(blok.link))) {
		return true;
	}
	return !!blok.children?.some((child) =>
		route.path.includes(getLink(child.link))
	);
});

function onPanelClose(close: () => void) {
	close();
	emit("closeMobileMenu");
}
</script>
